import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';

// GraphQL: Get site logo and downloadable CV
const GET_WEBSITE_OPTIONS = gql`
  query GetWebsiteOptions {
    websiteOptions {
      nodes {
        websiteOptions {
          logo {
            node {
              sourceUrl
            }
          }
          dow {
            node {
              mediaItemUrl
            }
          }
        }
      }
    }
  }
`;

// GraphQL: Get menu items from CPT
const GET_MENU_ITEMS = gql`
  query GetTopNavBarMenu {
    topNavBarMenus {
      nodes {
        title
        menuItems {
          order
          url
        }
      }
    }
  }
`;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Fetch GraphQL data
  const { data: optionsData } = useQuery(GET_WEBSITE_OPTIONS);
  const { data: menuData } = useQuery(GET_MENU_ITEMS);

  // Extract logo and download URL
  const logoUrl = optionsData?.websiteOptions?.nodes?.[0]?.websiteOptions?.logo?.node?.sourceUrl || '/images/logo.png';
  const cvUrl = optionsData?.websiteOptions?.nodes?.[0]?.websiteOptions?.dow?.node?.mediaItemUrl || '/files/naveedahmedawancv.pdf';

  // Prepare and sort menu items
  const allMenuItems =
    menuData?.topNavBarMenus?.nodes?.map((item) => ({
      title: item.title,
      order: item.menuItems?.order || 0,
      url: item.menuItems?.url || '#',
    }))?.sort((a, b) => a.order - b.order) || [];

  const menuItems = allMenuItems.filter(item => item.title.toLowerCase() !== 'contact');
  const contactItem = allMenuItems.find(item => item.title.toLowerCase() === 'contact');

  // Sticky effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLinkClick = () => {
    if (isMenuOpen) setIsMenuOpen(false);
  };

  const handleMouseLeave = () => {
    if (window.innerWidth <= 768) setIsMenuOpen(false);
  };

  return (
    <nav
      className={`bg-transparent p-4 relative z-50 transition-all duration-300 ${
        isSticky ? 'fixed top-0 left-0 w-full backdrop-blur-md bg-white/30' : 'absolute top-0 left-0 w-full'
      }`}
    >
      <div className="max-w-6xl mx-auto flex justify-between items-center">
        {/* Logo */}
        <Link to="/">
          <img src={logoUrl} alt="Logo" className="h-12" />
        </Link>

        {/* Mobile menu toggle */}
        <button
          ref={buttonRef}
          className="text-gray-800 md:hidden z-50"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <i className={isMenuOpen ? 'fas fa-arrow-right' : 'fas fa-bars'}></i>
        </button>

        {/* Navigation Menu */}
        <ul
          ref={menuRef}
          className={`${
            isMenuOpen ? 'flex flex-col space-y-4 z-50' : 'hidden'
          } md:flex md:flex-row md:space-x-6 md:space-y-0 absolute md:static top-16 left-0 w-full bg-white md:bg-transparent md:shadow-none md:p-0 md:h-auto p-4 transition-all duration-300 ease-in-out md:justify-end`}
          onMouseLeave={handleMouseLeave}
        >
          {/* Dynamic Menu Items */}
          {menuItems.map((item, index) => (
            <li key={index} className="flex items-center">
              <Link
                to={item.url}
                className="text-[#1E1E1E] hover:text-gray-500"
                onClick={handleLinkClick}
              >
                {item.title}
              </Link>
            </li>
          ))}

          {/* Contact as Button */}
          {contactItem && (
            <li className="flex items-center">
              <Link
                to={contactItem.url}
                className="flex items-center text-white bg-[#DABA60] py-2 px-6 hover:bg-[#c69e55] transition-all duration-300 ease-in-out"
                onClick={handleLinkClick}
              >
                {contactItem.title}
                <img
                  src="/images/rightarrow.png"
                  alt="Arrow"
                  className="ml-2 max-w-[24px] max-h-[24px]"
                />
              </Link>
            </li>
          )}

          {/* Download CV Button */}
          <li className="flex items-center">
            <a
              href={cvUrl}
              download
              className="flex items-center text-white bg-[#213343] py-2 px-6 hover:bg-[#1b2b34] transition-all duration-300 ease-in-out"
              onClick={handleLinkClick}
            >
              Download CV
              <img
                src="/images/rightarrow.png"
                alt="Arrow"
                className="ml-2 max-w-[24px] max-h-[24px]"
              />
            </a>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
